import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ensureLocalProfile, getLocalSessionUser } from '@/lib/local-crm';
import { ensureSupabaseProfile, getSupabaseSessionUser } from '@/lib/supabase-crm';
import { useOrgStore } from '@/store/orgStore';
import { dataMode, isDemoAuthMode, isLocalSurface, isQaSurface, isSupabaseAuthMode } from '@/config/runtime';
import { supabase } from '@/utils/supabase/client';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  memberships: Array<{ org_id: string; org_name: string; role: string }>;
  permissions: Record<string, string[]>;
}

interface AuthContextType {
  user: User | null | false;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (module: string, action: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

function formatApiError(detail: any): string {
  if (detail == null) return 'Ocurrio un error. Intente nuevamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e: any) => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const LOCAL_DEMO_USERS: Record<string, { password: string; id: string; name: string }> = {
  'admin@conectacrm.cl': {
    password: 'admin123',
    id: 'demo_admin',
    name: 'Administrador',
  },
  'jluis.delgado@gmail.com': {
    password: 'JLuis2026!',
    id: 'demo_jluis',
    name: 'Jose Luis Delgado',
  },
  'naviakaren@gmail.com': {
    password: 'Naviakaren2026!',
    id: 'demo_navia',
    name: 'Navia Karen',
  },
};

const LOCAL_DEMO_SESSION_KEY = 'conectacrm.local.auth.user';

function isLocalHostSession() {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
}

function isDemoLoginEnabled(email?: string) {
  if (!isDemoAuthMode) {
    return false;
  }
  if (isLocalSurface || isLocalHostSession()) {
    return true;
  }
  return email ? isSharedDemoEmail(email) : false;
}

function getLocalDemoUser(email: string, password: string) {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedPassword = password.trim().toLowerCase();
  const candidate = LOCAL_DEMO_USERS[normalizedEmail];
  const passwordMatches = candidate
    ? candidate.password.trim().toLowerCase() === normalizedPassword
    : false;
  if (!candidate || !passwordMatches || !isDemoLoginEnabled(normalizedEmail)) {
    return null;
  }
  return {
    id: candidate.id,
    email: normalizedEmail,
    user_metadata: { name: candidate.name },
  };
}

function isSharedDemoEmail(email: string) {
  const normalized = email.trim().toLowerCase();
  return ['jluis.delgado@gmail.com', 'naviakaren@gmail.com', 'admin@conectacrm.cl'].includes(normalized);
}

function getE2EAuthUser() {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem('conectacrm.e2e.user');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getStoredLocalDemoUser() {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(LOCAL_DEMO_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    window.localStorage.removeItem(LOCAL_DEMO_SESSION_KEY);
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null | false>(null);
  const { setOrganizations, setCurrentOrg, clear } = useOrgStore();

  const handleUserData = useCallback((userData: User) => {
    setUser(userData);
    if (userData.memberships?.length > 0) {
      setOrganizations(userData.memberships);
      // Sincronizar SIEMPRE el store con la org activa. Durante el registro, el
      // onboarding ya escribió currentOrgId en localStorage, pero el store Zustand
      // se creó antes (con null); sin esto el dashboard no dispara la carga hasta
      // un reload. Usamos la org de localStorage si está entre las membresías,
      // o la primera membresía como fallback.
      const stored = typeof window !== 'undefined' ? localStorage.getItem('currentOrgId') : null;
      const match = userData.memberships.find((m) => m.org_id === stored) || userData.memberships[0];
      setCurrentOrg(match.org_id, match.org_name);
    }
  }, [setOrganizations, setCurrentOrg]);

  const hydrateFromSupabaseUser = useCallback(async (authUser: any) => {
    if (dataMode === 'supabase') {
      const profile = await getSupabaseSessionUser(authUser.id).catch(() => null)
        || await ensureSupabaseProfile(authUser).catch(() => null);
      if (profile) {
        handleUserData(profile as User);
        return;
      }
    }
    const localUser = getLocalSessionUser(authUser.id) || ensureLocalProfile(authUser);
    handleUserData(localUser as User);
  }, [handleUserData]);

  const ensureSharedDemoProfile = useCallback((authUser: any, preferredName?: string) => {
    const localUser = getLocalSessionUser(authUser.id) || ensureLocalProfile(authUser, preferredName);
    if (isSharedDemoEmail(authUser.email || '')) {
      const dbUser = getLocalSessionUser(authUser.id) || localUser;
      if (dbUser?.memberships?.[0]?.org_id !== 'org_demo') {
        // ensure demo users converge on the same tenant
        const demoUser = ensureLocalProfile({ ...authUser, id: authUser.id }, preferredName);
        handleUserData(demoUser as User);
        return;
      }
    }
    handleUserData(localUser as User);
  }, [handleUserData]);

  useEffect(() => {
    const localDemoUser = getStoredLocalDemoUser();
    if (localDemoUser?.id && localDemoUser?.email) {
      ensureSharedDemoProfile(localDemoUser, localDemoUser.user_metadata?.name);
      return;
    }

    const e2eUser = getE2EAuthUser();
    if (e2eUser?.id && e2eUser?.email) {
      ensureSharedDemoProfile(e2eUser, e2eUser.name);
      return;
    }

    if (isDemoAuthMode) {
      setUser(false);
      return;
    }

    if (!isSupabaseAuthMode || !supabase) {
      setUser(false);
      return;
    }

    let isMounted = true;

    supabase.auth.getSession().then(({ data, error }) => {
      if (!isMounted) return;
      if (error || !data.session?.user) {
        setUser(false);
        return;
      }
      hydrateFromSupabaseUser(data.session.user);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      if (!session?.user) {
        setUser(false);
        clear();
        return;
      }
      hydrateFromSupabaseUser(session.user);
    });

    return () => {
      isMounted = false;
      subscription.subscription.unsubscribe();
    };
  }, [clear, ensureSharedDemoProfile, handleUserData, hydrateFromSupabaseUser]);

  const login = async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const localDemoUser = getLocalDemoUser(email, password);
    if (localDemoUser) {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LOCAL_DEMO_SESSION_KEY, JSON.stringify(localDemoUser));
      }
      ensureSharedDemoProfile(localDemoUser, localDemoUser.user_metadata.name);
      return;
    }

    if (!supabase) {
      if (isDemoAuthMode || isQaSurface || isSharedDemoEmail(normalizedEmail)) {
        throw new Error('Este QA usa acceso demo compartido. Revisa el correo y la clave de prueba.');
      }
      throw new Error('La autenticacion remota no esta disponible en este entorno.');
    }
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error || !data.user) {
        throw new Error(error?.message || 'No fue posible iniciar sesion.');
      }
      if (dataMode === 'supabase') {
        const profile = await getSupabaseSessionUser(data.user.id).catch(() => null)
          || await ensureSupabaseProfile(data.user).catch(() => null);
        if (profile) { handleUserData(profile as User); return; }
      }
      const localUser = ensureLocalProfile(data.user);
      handleUserData(localUser as User);
    } catch (e: any) {
      throw new Error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const register = async (name: string, email: string, password: string) => {
    if (!supabase) {
      if (isDemoAuthMode || isQaSurface) {
        throw new Error('El registro no esta habilitado en QA demo.');
      }
      throw new Error('La autenticacion remota no esta disponible en este entorno.');
    }
    try {
      const signUp = await supabase.auth.signUp({
        email,
        password,
        options: { data: { name } },
      });
      if (signUp.error) {
        throw new Error(signUp.error.message);
      }

      let authUser = signUp.data.user || null;
      if (!signUp.data.session) {
        if (signUp.data.user && !signUp.data.user.confirmed_at) {
          throw new Error('Revisa tu email y confirma tu cuenta antes de continuar.');
        }
        const signIn = await supabase.auth.signInWithPassword({ email, password });
        if (signIn.error) {
          throw new Error(signIn.error.message);
        }
        authUser = signIn.data.user;
      }

      if (!authUser) {
        throw new Error('No fue posible crear la sesion del usuario.');
      }

      if (dataMode === 'supabase') {
        const profile = await ensureSupabaseProfile(authUser, name).catch((err) => {
          console.error('[register] ensureSupabaseProfile falló:', err);
          return null;
        });
        if (profile) { handleUserData(profile as User); return; }
        throw new Error('No fue posible crear el perfil en Supabase. Revisa la consola.');
      }
      const localUser = ensureLocalProfile(authUser, name);
      handleUserData(localUser as User);
    } catch (e: any) {
      console.error('[register] error:', e);
      const detail = e.response?.data?.detail;
      throw new Error(detail ? formatApiError(detail) : e.message || 'Ocurrio un error. Intente nuevamente.');
    }
  };

  const logout = async () => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('conectacrm.e2e.user');
      window.localStorage.removeItem(LOCAL_DEMO_SESSION_KEY);
    }
    if (supabase) {
      await supabase.auth.signOut();
    }
    setUser(false);
    clear();
  };

  const hasPermission = (module: string, action: string): boolean => {
    if (!user) return false;
    const perms = user.permissions || {};
    return (perms[module] || []).includes(action);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
